import { Component, Host, Prop, State, h } from "@stencil/core";
import lottie, { AnimationItem } from "lottie-web";
import type {
  ImageData,
  VideoData,
  VideosData,
  ImagesData,
} from "../../../types/media";
import { isMobile } from "../../../utilities/screen";
import { prefersReducedMotion } from "../../../utilities/user";

export type GradientDirection = "top" | "bottom" | "left" | "right";

const BACKDROP_MODE_VIDEO = "video";
const BACKDROP_MODE_IMAGE = "image";
const BACKDROP_MODE_LOTTIE = "lottie";
const BACKDROP_MODE_SDX = "sdx";
const BACKDROP_MODE_EMPTY = "empty";
const BACKDROP_MODE_COLOR = "color";

@Component({
  tag: "scs-card-shell",
  styleUrl: "card-shell.scss",
  shadow: true,
})
export class CardShell {
  @Prop()
  hasShadow = false;

  @Prop({ mutable: true })
  gradientDirection?: GradientDirection;

  @Prop()
  backgroundImage?: string;
  private backgroundImageData?: ImagesData;

  @Prop()
  backgroundVideo?: string;
  private backgroundVideoData?: VideosData;

  @Prop()
  backgroundLottie?: string;
  private backgroundLottieData?: string;

  @Prop()
  sdxGradient?: string;

  @Prop()
  backgroundColor?: string;

  @State()
  styleProps: { [key: string]: string | undefined } = {};

  backdropLottieEl!: HTMLDivElement;

  gradientRootColor?: string;

  private lottieAnimation?: AnimationItem;

  contentEl!: HTMLElement;

  shellEl!: HTMLElement;

  getImage(): ImageData | null {
    if (
      !this.backgroundImageData?.desktop?.src &&
      !this.backgroundImageData?.desktop?.srcset
    ) {
      return null;
    }

    if (!isMobile()) {
      return this.backgroundImageData!.desktop;
    }

    if (
      this.backgroundImageData!.mobile?.src ||
      this.backgroundImageData!.mobile?.srcset
    ) {
      return this.backgroundImageData!.mobile;
    }

    return this.backgroundImageData!.desktop;
  }

  getVideo(): VideoData | null {
    if (!this.backgroundVideoData?.desktop?.src) {
      return null;
    }

    if (!isMobile()) {
      return this.backgroundVideoData!.desktop;
    }

    if (this.backgroundVideoData!.mobile?.src) {
      return this.backgroundVideoData!.mobile;
    }

    return this.backgroundVideoData!.desktop;
  }

  getStyleProps() {
    if (!this.gradientDirection) this.gradientDirection = "top";

    const props: { [key: string]: string | undefined } = {};

    this.gradientRootColor =
      this.backgroundImageData?.desktop?.colorOverlay || undefined;

    if (isMobile() && this.backgroundImageData?.mobile) {
      this.gradientRootColor = this.backgroundImageData.mobile.colorOverlay;
    }

    // Construct background image with image and gradient if set
    const backGroundImageLayers: string[] = [];

    if (this.gradientRootColor) {
      const parentHeight = this.shellEl?.offsetHeight;
      const contentTop = this.contentEl?.offsetTop;
      let gradientExtent = 50;

      if (parentHeight && contentTop) {
        gradientExtent = (1 - contentTop / parentHeight) * 100;
      }

      backGroundImageLayers.push(`linear-gradient(
          in hsl to ${this.gradientDirection},
          hsl(from ${this.gradientRootColor} h s 15),
          hsl(from ${this.gradientRootColor} h s 15 / 50%) ${gradientExtent * 0.8}%,
          hsl(from ${this.gradientRootColor} h s 15 / 25%) ${gradientExtent * 0.9}%,
          hsl(from ${this.gradientRootColor} h s 15 / 0%) ${gradientExtent}%
        )`);
    }

    props["background-image"] = backGroundImageLayers.join(", ");
    (props["background-color"] =
      this.renderBackdrop() == BACKDROP_MODE_COLOR ? this.backgroundColor : ""),
      (this.styleProps = props);
  }

  getShellClasses() {
    return [
      "card-shell",
      this.hasShadow ? "card-shell--shadow" : "",
      this.gradientRootColor ? `card-shell--${this.gradientDirection}` : "",
    ].join(" ");
  }

  getContentClasses() {
    return [
      "card-content",
      this.gradientRootColor ? `card-content--${this.gradientDirection}` : "",
    ].join(" ");
  }

  getBackdropClasses() {
    return [
      "card-backdrop",
      this.renderBackdrop() == BACKDROP_MODE_SDX ? this.sdxGradient : "",
      this.renderBackdrop() == BACKDROP_MODE_EMPTY ? "empty" : "",
    ].join(" ");
  }

  componentWillRender() {
    this.getStyleProps();
  }

  public async componentWillLoad(): Promise<void> {
    if (this.backgroundImage) {
      const dataObj = JSON.parse(this.backgroundImage);
      if (!dataObj.desktop && dataObj.src) {
        this.backgroundImageData = {
          desktop: { ...dataObj },
        };
      } else {
        this.backgroundImageData = { ...dataObj };
      }
    }
    if (this.backgroundVideo) {
      this.backgroundVideoData = JSON.parse(this.backgroundVideo);
    }
    if (this.backgroundLottie) {
      this.backgroundLottieData = decodeURI(this.backgroundLottie);
    }
    this.getStyleProps();
  }

  componentDidLoad() {
    if (this.backdropLottieEl) {
      this.lottieAnimation = lottie.loadAnimation({
        container: this.backdropLottieEl,
        renderer: "svg",
        loop: true,
        autoplay: !prefersReducedMotion(),
        animationData: JSON.parse(this.backgroundLottieData as string),
      });
    }
    this.getStyleProps();
  }

  disconnectedCallback() {
    if (this.lottieAnimation) {
      this.lottieAnimation.destroy();
    }
  }

  renderBackdrop(): string {
    if (this.backgroundImage != null) {
      return BACKDROP_MODE_IMAGE;
    }

    if (this.getVideo()) {
      return BACKDROP_MODE_VIDEO;
    }

    if (this.backgroundLottie) {
      return BACKDROP_MODE_LOTTIE;
    }

    if (this.sdxGradient) {
      return BACKDROP_MODE_SDX;
    }

    if (this.backgroundColor) {
      return BACKDROP_MODE_COLOR;
    }

    return BACKDROP_MODE_EMPTY;
  }

  render() {
    return (
      <Host>
        <article
          class={this.getShellClasses()}
          style={this.styleProps}
          part="shell"
          ref={el => (this.shellEl = el as HTMLElement)}
        >
          <section
            class={this.getContentClasses()}
            part="content"
            ref={el => (this.contentEl = el as HTMLElement)}
          >
            <slot></slot>
          </section>
          <section class="card-image" part="image">
            <slot name="image-slot"></slot>
          </section>
        </article>
        <div class={this.getBackdropClasses()} part="backdrop">
          {this.renderBackdrop() == BACKDROP_MODE_IMAGE && (
            <img
              {...{
                src: this.getImage()!.src,
                alt: this.getImage()!.alt || "",
                srcset: this.getImage()!.srcset,
              }}
              class="card-backdrop__img"
            />
          )}

          {this.renderBackdrop() == BACKDROP_MODE_VIDEO && (
            <video
              {...{
                muted: true,
                playsinline: true,
                loop: true,
                autoplay: !prefersReducedMotion(),
              }}
              class="card-backdrop__video"
            >
              {this.getVideo() && (
                <source
                  src={this.getVideo()!.src}
                  type={this.getVideo()!.type}
                  media="(max-width: 768)"
                />
              )}
              <source src={this.getVideo()!.src} type={this.getVideo()!.type} />
            </video>
          )}

          {this.renderBackdrop() == BACKDROP_MODE_LOTTIE && (
            <figure
              class="card-backdrop__figure"
              ref={el => (this.backdropLottieEl = el as HTMLDivElement)}
            />
          )}
        </div>
      </Host>
    );
  }
}
